import { Box, Icon } from "@outschool/backpack";
import { REFER_CREDIT_CENTS } from "@outschool/business-rules";
import { User } from "@outschool/gql-backend-generated";
import {
  CurrentUserQueryQuery,
  ShareContainer_ActivityFragment,
} from "@outschool/gql-frontend-generated";
import { faEnvelope, faLink } from "@outschool/icons";
import {
  Trans,
  renderCurrencyString,
  useTranslation,
} from "@outschool/localization";
import { addParamsToUrl } from "@outschool/routes";
import { gql } from "@outschool/ui-apollo";
import { TrackedButton } from "@outschool/ui-components-shared";
import {
  CopyToClipboardButton,
  uiContextIds,
} from "@outschool/ui-components-website";
import { Container } from "@outschool/ui-legacy-component-library";
import { useBooleanState } from "@outschool/ui-utils";
import React, { useCallback } from "react";

import * as Routes from "../../../shared/Routes";
import { EnrolledClassSection } from "../enrolled/EnrolledClassTypes";
import Link from "../Link";
import EmailModal from "./EmailModal";
import { ActivitySendDialog, ActivityShareDialog } from "./Facebook";

interface ShareContainerProps {
  activity: ShareContainer_ActivityFragment;
  section?: EnrolledClassSection;
  user: CurrentUserQueryQuery["currentUser"] | User;
  showReferralCTA?: boolean;
  onEmailShareClose?: () => void;
  currentUserIsEnrolled?: boolean;
}

function ShareContainer({
  activity,
  section,
  user,
  showReferralCTA = true,
  onEmailShareClose,
  currentUserIsEnrolled = false,
}: ShareContainerProps) {
  const { t } = useTranslation("client\\components\\sharing\\ShareContainer");
  const [isEmailModalOpen, setEmailModalOpen, setEmailModalClosed] =
    useBooleanState(false);

  const onEmailClose = useCallback(() => {
    onEmailShareClose && onEmailShareClose();
    setEmailModalClosed();
  }, [onEmailShareClose, setEmailModalClosed]);

  const creditCashAmount = renderCurrencyString({
    priceInCents: REFER_CREDIT_CENTS,
    showCurrencyCode: false,
  });

  const onPreviewClick = React.useCallback(
    (e: $TSFixMe) => {
      e.preventDefault();
      setEmailModalOpen();
    },
    [setEmailModalOpen],
  );
  const ActivityShareButtons = activity && (
    <>
      <ActivityShareDialog
        activity={activity}
        section={section}
        // @ts-ignore TS(2322): Type '{ __typename?: "User" | undefined; uid: stri... Remove this comment to see the full error message
        referrer={user}
        label={t("Share to Facebook or Group")}
        segmentEventName="Share activity to Facebook clicked"
        quote={t`Hi! We signed up for this class on Outschool. It would be great if you could join us!`}
      />
      <ActivitySendDialog
        activity={activity}
        section={section}
        // @ts-ignore TS(2322): Type '{ __typename?: "User" | undefined; uid: stri... Remove this comment to see the full error message
        referrer={user}
        fullWidth
      />
      <TrackedButton
        fullWidth
        title={t("Invite by email")}
        onClick={onPreviewClick}
        startIcon={
          <Icon
            icon={faEnvelope}
            sx={{
              fontSize: "1.333em",
            }}
          />
        }
        trackingName="share_via_email"
        trackingUniqueId={activity.uid}
      >
        {t("Invite by email")}
      </TrackedButton>
      <CopyToClipboardButton
        icon={faLink}
        uiContextId={uiContextIds.ClassPagePostEnroll}
        trackingName="share_link_copy_button"
        trackingUniqueId={activity.uid}
        value={addParamsToUrl(
          Routes.sectionOrActivityUrl(activity, section, undefined, true),
          {
            usid: user && user.slug_id,
            signup: true,
            utm_campaign: "share_activity_link",
          },
        )}
        label={t("Copy Class Link")}
      />
      <EmailModal
        isOpen={isEmailModalOpen}
        onClose={onEmailClose}
        activityUid={activity.uid}
        sectionUid={section?.uid}
        currentUserIsEnrolled={currentUserIsEnrolled}
      />
    </>
  );

  return (
    <Container
      fullBleed
      sx={{
        borderRadius: 8,
        paddingY: "medium",
        textAlign: "center",
      }}
    >
      {showReferralCTA && (
        <Box
          sx={{
            size: "small",
            color: "grey.500",
            marginBottom: "1em",
          }}
        >
          <React.Fragment>
            <Trans t={t} values={{ creditCashAmount }}>
              <Link
                to={Routes.invitePath()}
                trackingName="parent_referral_link"
                trackingUniqueId={`${user && user.uid}_parent_referral_link`}
              >
                Give {{ creditCashAmount }} Get {{ creditCashAmount }}
              </Link>{" "}
              referral program.
            </Trans>
          </React.Fragment>
        </Box>
      )}

      <Box
        flex
        sx={{
          flexDirection: "column",
          gap: "0.5em",
        }}
      >
        {ActivityShareButtons}
      </Box>
    </Container>
  );
}

ShareContainer.fragments = {
  activity: gql`
    fragment ShareContainer_activity on Activity {
      uid
      slug_id
      title
      titleTranslated
    }
  `,
};

export default ShareContainer;
